import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import React, { forwardRef } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import styles from './input.module.scss';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Indicates whether the field is disabled */
  disabled?: boolean;
  /** A text that is shown below the field and affects the styling */
  error?: string;
  /** Indicates whether the field has a clear button */
  hasClear?: boolean;
  /** A text that is shown below the field as help text */
  help?: string;
  /** The icon to show inside the input field */
  icon?: IconName;
  /** The text that is shown above the field as a label */
  label?: string;
  /** The max value of the field */
  max?: string | number | undefined;
  /** The min value of the field */
  min?: string | number | undefined;
  /** An event that is triggered on blur */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** An event that is triggered on change */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** An event that is triggered on clear */
  onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** An event that is triggered on pressing enter key */
  onEnter?: () => void;
  /** An event that is triggered on focus */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** The placeholder for the input field */
  placeholder?: string;
  /** The value of the field */
  value?: string;
  /** The variant of the input */
  variant?: 'default' | 'search';
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      disabled,
      error,
      hasClear,
      help,
      icon,
      id,
      label,
      max,
      min,
      onClear,
      onEnter,
      placeholder,
      required,
      value,
      variant = 'default',
      type,
      ...rest
    },
    ref,
  ) => {
    const { t } = useI18n();

    return (
      <div
        className={classNames(styles.component, className, {
          [styles.hasClear]: hasClear,
          [styles.hasError]: error,
          [styles.hasIcon]: icon,
          [styles.hasLabel]: label,
          [styles.isDisabled]: disabled,
          [styles.isSearch]: variant === 'search',
        })}
      >
        <div className={styles.fieldWrapper}>
          {icon && <Icon className={styles.icon} name={icon} size={24} />}

          {label && (
            <Typography
              className={styles.label}
              htmlFor={id}
              tag="label"
              tagStyle="bodySmallBold"
            >
              {label}

              {required && <span className={styles.requiredMark}> *</span>}
            </Typography>
          )}

          {hasClear && value && value.length > 0 && (
            <button
              aria-label={t('items.common.words.close')}
              className={styles.clear}
              onClick={onClear}
            >
              <Icon name="Close" size={24} />
            </button>
          )}

          {type === 'date' && (
            <Icon className={styles.calendar} name="Calendar" size={24} />
          )}

          <input
            {...rest}
            ref={ref}
            className={styles.field}
            disabled={disabled}
            id={id}
            max={max}
            min={min}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                onEnter && onEnter();
              }
            }}
            placeholder={placeholder}
            type={type}
            value={value}
          />
        </div>

        {(error || help) && (
          <Typography className={styles.help} tagStyle="bodySmall">
            {error || help}
          </Typography>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
